/**
 * Workflow engine (SRS §4 "Workflow configuration", "Approval matrices", §24.2).
 *
 * A workflow is data (stored in workflow_definitions.definition), not code:
 *   states      — the values the record's status field may take
 *   transitions — action, from-states, to-state, required permission, guards,
 *                 optional multi-step approval
 *
 * When a transition carries `approval`, applying it does not move the record
 * to `to` immediately: it moves to `pendingState` (default 'pending_approval'),
 * and an approval request is created with the given steps. Approving the last
 * step completes the transition; a rejection moves the record to `onReject`.
 */
import type { PermissionSet } from '../auth/permissions.ts';

export type StateColor = 'gray' | 'blue' | 'amber' | 'green' | 'red' | 'purple' | 'teal';

export interface WorkflowState {
  code: string;
  label: string;
  /** Arabic label; the i18n extractor reports states without one */
  labelAr?: string;
  color: StateColor;
  isFinal?: boolean;
}

export type GuardOp = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'empty' | 'not_empty';

export interface GuardCondition {
  field: string;
  op: GuardOp;
  value?: unknown;
  /** message shown when the guard blocks the action */
  message?: string;
}

export interface ApprovalStep {
  step: number;
  label: string;
  /** any of these roles may act on the step */
  roles?: string[];
  /** …or anyone holding this permission */
  permission?: string;
  /** optional threshold: step only required when field > value (e.g. amount above 100,000) */
  when?: GuardCondition[];
}

export interface ApprovalSpec {
  steps: ApprovalStep[];
  onReject: string;
}

export interface WorkflowTransition {
  action: string;
  label: string;
  /** Arabic label; the i18n extractor reports transitions without one */
  labelAr?: string;
  from: string[];
  to: string;
  permission?: string;
  guards?: GuardCondition[];
  approval?: ApprovalSpec;
  pendingState?: string;
  requiresReason?: boolean;
  /** visual hint for the button */
  variant?: 'primary' | 'secondary' | 'destructive';
}

export interface WorkflowDefinition {
  entity: string;
  statusField?: string;
  initial: string;
  states: WorkflowState[];
  transitions: WorkflowTransition[];
}

export interface WorkflowActor {
  permissions: PermissionSet;
  roles: string[];
  userId: string;
}

export type Record_ = Record<string, unknown>;

// ----------------------------------------------------------------------------
// Guards
// ----------------------------------------------------------------------------

function toComparable(v: unknown): number | string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'bigint') return Number(v);
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'string') {
    const n = Number(v);
    return v.trim() !== '' && !Number.isNaN(n) ? n : v;
  }
  if (typeof v === 'object' && 'toString' in v) return toComparable(String(v));
  return String(v);
}

export function evaluateGuard(c: GuardCondition, record: Record_): boolean {
  const v = record[c.field];
  switch (c.op) {
    case 'empty': return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
    case 'not_empty': return !evaluateGuard({ ...c, op: 'empty' }, record);
    case 'eq': return toComparable(v) === toComparable(c.value);
    case 'neq': return toComparable(v) !== toComparable(c.value);
    case 'in': return Array.isArray(c.value) && c.value.map(toComparable).includes(toComparable(v));
    case 'gt': case 'gte': case 'lt': case 'lte': {
      const a = toComparable(v), b = toComparable(c.value);
      if (a === null || b === null) return false;
      if (c.op === 'gt') return a > b;
      if (c.op === 'gte') return a >= b;
      if (c.op === 'lt') return a < b;
      return a <= b;
    }
    default: return false;
  }
}

export function failingGuards(conditions: GuardCondition[] | undefined, record: Record_): GuardCondition[] {
  return (conditions ?? []).filter((c) => !evaluateGuard(c, record));
}

// ----------------------------------------------------------------------------
// Transitions
// ----------------------------------------------------------------------------

export function currentStatus(def: WorkflowDefinition, record: Record_): string {
  const field = def.statusField ?? 'status';
  return String(record[field] ?? def.initial);
}

export function stateOf(def: WorkflowDefinition, code: string): WorkflowState | undefined {
  return def.states.find((s) => s.code === code);
}

export interface AvailableTransition {
  transition: WorkflowTransition;
  allowed: boolean;
  blockedBy: 'permission' | 'guard' | null;
  guardMessages: string[];
}

/** Transitions from the record's current state, with the reason when blocked. */
export function availableTransitions(def: WorkflowDefinition, record: Record_, actor: WorkflowActor): AvailableTransition[] {
  const status = currentStatus(def, record);
  return def.transitions
    .filter((t) => t.from.includes(status) || t.from.includes('*'))
    .map((t) => {
      if (t.permission && !actor.permissions.has(t.permission)) {
        return { transition: t, allowed: false, blockedBy: 'permission' as const, guardMessages: [] };
      }
      const failing = failingGuards(t.guards, record);
      if (failing.length) {
        return { transition: t, allowed: false, blockedBy: 'guard' as const,
                 guardMessages: failing.map((g) => g.message ?? `Condition on ${g.field} not met`) };
      }
      return { transition: t, allowed: true, blockedBy: null, guardMessages: [] };
    });
}

export class WorkflowError extends Error {
  constructor(message: string, readonly code: 'unknown_action' | 'invalid_state' | 'forbidden' | 'guard_failed' | 'reason_required') {
    super(message);
    this.name = 'WorkflowError';
  }
}

export interface TransitionResult {
  /** status to write on the record now */
  status: string;
  /** when set, an approval request must be created with these steps */
  approval?: { steps: ApprovalStep[]; finalStatus: string; onReject: string };
  transition: WorkflowTransition;
}

/** Validates and computes the effect of an action. Pure: does not touch storage. */
export function applyTransition(
  def: WorkflowDefinition, record: Record_, action: string, actor: WorkflowActor, reason?: string,
): TransitionResult {
  const t = def.transitions.find((x) => x.action === action);
  if (!t) throw new WorkflowError(`Unknown action "${action}" for ${def.entity}`, 'unknown_action');
  const status = currentStatus(def, record);
  if (!t.from.includes(status) && !t.from.includes('*')) {
    throw new WorkflowError(`Cannot ${t.label.toLowerCase()} a ${def.entity} in state "${status}"`, 'invalid_state');
  }
  if (t.permission && !actor.permissions.has(t.permission)) {
    throw new WorkflowError(`Missing permission ${t.permission}`, 'forbidden');
  }
  const failing = failingGuards(t.guards, record);
  if (failing.length) {
    throw new WorkflowError(failing.map((g) => g.message ?? `Condition on ${g.field} not met`).join('; '), 'guard_failed');
  }
  if (t.requiresReason && !(reason && reason.trim())) {
    throw new WorkflowError(`A reason is required to ${t.label.toLowerCase()}`, 'reason_required');
  }
  if (t.approval) {
    const steps = t.approval.steps.filter((s) => !s.when || failingGuards(s.when, record).length === 0);
    if (steps.length > 0) {
      return {
        status: t.pendingState ?? 'pending_approval',
        approval: { steps, finalStatus: t.to, onReject: t.approval.onReject },
        transition: t,
      };
    }
  }
  return { status: t.to, transition: t };
}

// ----------------------------------------------------------------------------
// Approval requests
// ----------------------------------------------------------------------------

export type ApprovalDecision = 'approved' | 'rejected' | 'returned';

export interface ApprovalStepState extends ApprovalStep {
  status: 'pending' | 'approved' | 'rejected' | 'skipped';
  decidedBy?: string;
  decidedAt?: string;
  comment?: string;
}

export interface ApprovalRequestState {
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  currentStep: number;
  steps: ApprovalStepState[];
  finalStatus: string;
  onReject: string;
}

export function newApprovalRequest(steps: ApprovalStep[], finalStatus: string, onReject: string): ApprovalRequestState {
  const ordered = [...steps].sort((a, b) => a.step - b.step);
  return {
    status: 'pending',
    currentStep: ordered[0]?.step ?? 1,
    steps: ordered.map((s) => ({ ...s, status: 'pending' })),
    finalStatus,
    onReject,
  };
}

/** Can this actor decide the current step? */
export function canActOnStep(req: ApprovalRequestState, actor: WorkflowActor): boolean {
  const step = req.steps.find((s) => s.step === req.currentStep);
  if (!step || req.status !== 'pending') return false;
  const byRole = step.roles?.some((r) => actor.roles.includes(r)) ?? false;
  const byPerm = step.permission ? actor.permissions.has(step.permission) : false;
  return byRole || byPerm || actor.permissions.has('admin.approval.approve');
}

export interface ApprovalOutcome {
  request: ApprovalRequestState;
  /** status to write on the underlying record, if it changes now */
  recordStatus?: string;
}

export function decideApproval(
  req: ApprovalRequestState, actor: WorkflowActor, decision: ApprovalDecision, comment?: string, now = new Date(),
): ApprovalOutcome {
  if (req.status !== 'pending') throw new WorkflowError('Approval request is already decided', 'invalid_state');
  if (!canActOnStep(req, actor)) throw new WorkflowError('You are not an approver for this step', 'forbidden');
  if (decision !== 'approved' && !(comment && comment.trim())) {
    throw new WorkflowError('A comment is required when rejecting or returning', 'reason_required');
  }
  const steps = req.steps.map((s) => s.step === req.currentStep
    ? { ...s, status: decision === 'approved' ? 'approved' as const : 'rejected' as const,
        decidedBy: actor.userId, decidedAt: now.toISOString(), comment }
    : s);
  if (decision !== 'approved') {
    return { request: { ...req, steps, status: 'rejected' }, recordStatus: req.onReject };
  }
  const next = steps.find((s) => s.step > req.currentStep && s.status === 'pending');
  if (next) return { request: { ...req, steps, currentStep: next.step } };
  return { request: { ...req, steps, status: 'approved' }, recordStatus: req.finalStatus };
}

// ----------------------------------------------------------------------------
// Templates
// ----------------------------------------------------------------------------

/** Master-data governance workflow (SRS §24): draft → pending_approval → approved. */
export function masterDataWorkflow(entity: string, module: string, approverRoles: string[]): WorkflowDefinition {
  return {
    entity,
    statusField: 'approval_status',
    initial: 'draft',
    states: [
      { code: 'draft', label: 'Draft', color: 'gray' },
      { code: 'pending_approval', label: 'Pending Approval', color: 'amber' },
      { code: 'approved', label: 'Approved', color: 'green', isFinal: true },
      { code: 'rejected', label: 'Rejected', color: 'red' },
    ],
    transitions: [
      { action: 'submit', label: 'Submit for Approval', from: ['draft', 'rejected'], to: 'approved',
        permission: `${module}.${entity}.submit`, variant: 'primary',
        approval: { steps: [{ step: 1, label: 'Approval', roles: approverRoles, permission: `${module}.${entity}.approve` }], onReject: 'rejected' } },
      { action: 'approve_direct', label: 'Approve', from: ['draft', 'rejected'], to: 'approved',
        permission: `${module}.${entity}.approve`, variant: 'primary' },
      { action: 'reopen', label: 'Return to Draft', from: ['approved', 'rejected'], to: 'draft',
        permission: `${module}.${entity}.edit`, requiresReason: true, variant: 'secondary' },
    ],
  };
}

/** Transactional document workflow: draft → submitted → approved → posted, with cancel. */
export function documentWorkflow(entity: string, module: string, opts: { approverRoles: string[]; approvalThresholdField?: string; approvalThreshold?: number } ): WorkflowDefinition {
  const approvalStep: ApprovalStep = { step: 1, label: 'Approval', roles: opts.approverRoles, permission: `${module}.${entity}.approve` };
  if (opts.approvalThresholdField && opts.approvalThreshold !== undefined) {
    approvalStep.when = [{ field: opts.approvalThresholdField, op: 'gt', value: opts.approvalThreshold }];
  }
  return {
    entity,
    initial: 'draft',
    states: [
      { code: 'draft', label: 'Draft', color: 'gray' },
      { code: 'pending_approval', label: 'Pending Approval', color: 'amber' },
      { code: 'approved', label: 'Approved', color: 'blue' },
      { code: 'posted', label: 'Posted', color: 'green', isFinal: true },
      { code: 'rejected', label: 'Rejected', color: 'red' },
      { code: 'cancelled', label: 'Cancelled', color: 'gray', isFinal: true },
    ],
    transitions: [
      { action: 'submit', label: 'Submit', from: ['draft', 'rejected'], to: 'approved', permission: `${module}.${entity}.submit`, variant: 'primary',
        approval: { steps: [approvalStep], onReject: 'rejected' } },
      { action: 'post', label: 'Post', from: ['approved'], to: 'posted', permission: `${module}.${entity}.post`, variant: 'primary' },
      { action: 'return_to_draft', label: 'Return to Draft', from: ['approved', 'rejected'], to: 'draft', permission: `${module}.${entity}.edit`, requiresReason: true },
      { action: 'cancel', label: 'Cancel', from: ['draft', 'approved', 'rejected'], to: 'cancelled', permission: `${module}.${entity}.edit`, requiresReason: true, variant: 'destructive' },
    ],
  };
}
